import * as React from 'react';
import { observer } from 'mobx-react';
import { ResponsiveModal } from '@estee/elc-responsive-modal';
import styled from 'styled-components';
import { RemoveIcon } from '@estee/elc-icons';
import { ITranslationsCollection, translate, configConsumer } from '@estee/elc-service';
import { CartLimitReachedModal } from '../cart-limit-modal/CartLimitReachedModal';
import { classes } from '../../utils/Classes';
import { ProductQuickViewController } from '../../controllers/ProductQuickViewController';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/ProductService';
import { Product } from '../../domain/entities/Product';
import { Sku } from '../../domain/entities/Sku';
import { IProductQuickviewConfig } from '../../interfaces/IProductQuickview';
import { OrderRender } from '@estee/elc-layouts';
import { getViewRegistry } from '../..//utils/orderableUtils/getViewRegistry';
import { IBaseFeatureData } from '../../interfaces/IOrderableComponents';
import classnames from 'classnames';
import { ComponentTypes } from '~constants/ComponentTypes';

export interface IProductQuickView {
    config?: IProductQuickviewConfig;
    translations?: ITranslationsCollection;
    onShadeSelect(name: string): void;
    onSizeSelect(name: string): void;
    onProductQuickViewClose(): void;
    product: Product;
    selectedSku: Sku;
}

const Wrapper = styled.div`
    margin-top: 25px;
    display: inline-block;
    width: 100%;
`;

const ProductQuickViewWrapper = styled(ResponsiveModal)`
    padding: 0;
    width: 96%;
`;

const ProductQuickViewCloseButton = styled.button`
    cursor: pointer;
    float: right;
    border: none;
    outline: none;
    margin: 10px;
    [dir='rtl'] & {
        float: left;
    }
`;

@configConsumer([
    'itemMaxQuantity',
    'hideSizeWhenSingular',
    'sizePicker',
    'installments',
    'freeShippingAndReturn',
    'shortDescription',
    'loyaltyLearnMore',
    'sitewideOnsaleMessage',
    'imageCarouselSettings',
    'showSubDisplayName',
    'componentElementsOrderConfig',
    'shadeSwatches',
    'showIncludeTaxLabel'
])
@translate([
    'autoReplenishLabel',
    'subscribeLabel',
    'subscribeCopyLabel',
    'selectFrequencyLabel',
    'notifyWhenAvailable',
    'taxIncluded',
    'shipsBy',
    'preorderNotAvailableMessage',
    'discountedPricePerUnitMessage',
    'discountPercentLabel',
    'onSaleTodayLabel',
    'closeLabel',
    'quickViewButtonLabel'
])
@observer
export class ProductQuickView extends React.Component<IProductQuickView> {
    public static displayName: string;
    private viewController: ProductQuickViewController;

    constructor(props: IProductQuickView) {
        super(props);

        this.viewController = diContainer.get(serviceNames.productQuickViewController);
        this.viewController.initialize(props.config || {});
        this.viewController.setProduct(props.product);
        this.viewController.setSelectedSkuId(props.selectedSku.skuId);
    }

    private closeProductQuickView = () => {
        this.props.onProductQuickViewClose();
    };

    private handleShadeSelect = (name: string) => {
        this.props.onShadeSelect(name);
        this.viewController.selectShade(name);
    };

    private handleSizeSelect = (name: string) => {
        this.props.onSizeSelect(name);
        this.viewController.selectSize(name);
    };

    public render() {
        const translations = this.props.translations as ITranslationsCollection;
        const { closeLabel, quickViewButtonLabel } = translations;
        const { productQuickview: productQuickviewConfig } =
            (this.props.config && this.props.config.componentElementsOrderConfig) || {};

        if (!productQuickviewConfig) {
            return null;
        }

        const { displayName } = this.viewController.data.product;

        const productQuickViewWrapperClassNames: string = classes('product-quick-view-wrapper');
        const productQuickViewCloseButtonClassNames: string = classes(
            'product-quick-view-close-button'
        );
        const productQuickViewCloseButtonIconClassNames: string = classes(
            'product-quick-view-close-button-icon'
        );
        const productQuickViewContainerWrapperClassNames: string = classes(
            'product-quick-view-container-wrapper'
        );
        const productQuickViewContentClassNames: string = classes('product-quick-view-content');
        const productQuickViewContentOrderableClassNames: string = classes(
            'product-quick-view-content-orderable'
        );

        const productQuickViewClassNames = classnames(
            productQuickViewContentClassNames,
            productQuickViewContentOrderableClassNames
        );

        const featureConfig: IBaseFeatureData = {
            ...this.viewController.data,
            translations: this.props.translations as ITranslationsCollection,
            setAutoReplenishOption: this.viewController.setAutoReplenishOption,
            getCustomSizeMedia: this.viewController.getCustomSizeMedia,
            navigateToProduct: this.viewController.navigateToProduct,
            handleShadeSelect: this.handleShadeSelect,
            handleSizeSelect: this.handleSizeSelect,
            emitSocialShareIconTriggered: this.viewController.emitSocialShareIconTriggered,
            selectShade: this.viewController.selectShade,
            handleLiveChatCTAClick: this.viewController.handleLiveChatCTAClick,
            handleFavoriteClick: this.viewController.handleFavoriteClick,
            productUpdateQuantity: this.viewController.productUpdateQuantity,
            closeModalAction: this.closeProductQuickView,
            clickAction: this.viewController.navigateToProduct,
            href: this.viewController.data.selectedProductUrl,
            componentType: ComponentTypes.PRODUCT_QUICKSHOP
        };

        return (
            <Wrapper className={productQuickViewContainerWrapperClassNames}>
                <ProductQuickViewWrapper
                    className={productQuickViewWrapperClassNames}
                    isOpen={true}
                    title={displayName}
                    deactivateModal={this.closeProductQuickView}
                    onMaskClick={this.closeProductQuickView}
                    underlayClickExits={false}
                >
                    <ProductQuickViewCloseButton
                        aria-label={`${closeLabel} ${quickViewButtonLabel}`}
                        value="close-quick-shop-button"
                        className={productQuickViewCloseButtonClassNames}
                        onClick={this.closeProductQuickView}
                        title={`${closeLabel} ${quickViewButtonLabel}`}
                    >
                        <RemoveIcon className={productQuickViewCloseButtonIconClassNames} />
                    </ProductQuickViewCloseButton>

                    <div className={productQuickViewClassNames} data-test-id="quickshop-content">
                        {Object.keys(productQuickviewConfig).map((sectionName: string) => (
                            <div className={classes(`${sectionName}-section`)} key={sectionName}>
                                <OrderRender
                                    data-test-id={`${sectionName}-section`}
                                    orderConfig={productQuickviewConfig[sectionName]}
                                    serviceName="elc-service-prodcat"
                                    viewRegistry={getViewRegistry<IBaseFeatureData>(featureConfig)}
                                />
                            </div>
                        ))}
                    </div>
                </ProductQuickViewWrapper>
                <CartLimitReachedModal />
            </Wrapper>
        );
    }
}
